import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Alert } from "../components/Alert";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Spinner } from "../components/Spinner";
import { ApiError } from "../lib/apiFetch";
import type {
  PartnerLinkInfoResponse,
  PartnerSetPasswordRequest,
  PartnerSetPasswordResponse,
} from "../types/api";
import { AuthCard } from "./AuthCard";
import { PasswordChecklist } from "./PasswordChecklist";
import { evaluatePasswordRules, isPasswordValid } from "./passwordPolicy";
import { setSignupPassword } from "./signupHandoff";
import { usePortalApi } from "./usePortalApi";
import { usePortalAuthConfig } from "./usePortalAuthConfig";
import { usePortalSession } from "./usePortalSession";

/**
 * Set-password from a magic link (`/set-password?code=…`).
 *
 * Reads the single-use `code` from the query string. On mount it probes
 * `GET <apiPrefix>/auth/link-info?code=…`, which is read-only and doesn't
 * consume the link, to learn whether the account behind the link is nameless
 * (`needsName`) and whether the form must collect a phone and run the OTP
 * (`firstTimeSetup`, true for an onboard or invite link whose account has no
 * verified phone yet). A GPO first-time link for an account whose phone is
 * already verified, as when someone joins a second GPO, comes back
 * `firstTimeSetup: false` and takes the session path below. The form waits on
 * that probe behind a spinner, so it can't render and then change, and the
 * user can't submit before the flags are known. A link-info failure defaults
 * both flags to false and renders the form; the 400 on submit then surfaces a
 * bad link.
 *
 * The phone field shows only when `firstTimeSetup` is true. It is gated on
 * that flag, not `needsName`, because a named org-member invite has
 * `needsName=false` but still needs the phone. The name field shows when the
 * account is nameless (`needsName`) and also on a first-time setup whose
 * invite stored a name, so the invitee can correct it. Both fields start from
 * the invite's stored values (`prefillName`, `prefillPhone`), editable.
 *
 * A live policy checklist keeps submit disabled until every rule passes and
 * any shown name and phone are non-blank. On submit:
 * - a `firstTimeSetup` link returns a `challenge`: stash the password in the
 *   in-memory hand-off and navigate to `/verify-phone` with
 *   `{ challenge, code, bypassSmsOtp }` in router state. No session is stored.
 * - a reset, or a first-time link for an account with a verified phone,
 *   returns a `session`: store it and navigate to the home page (`/`).
 *
 * The subtitle comes from the portal config's `copy.setPasswordSubtitle`.
 *
 * Errors:
 * - missing `code`: an error state that never POSTs.
 * - 400: the link is invalid, expired or already used, so the message directs
 *   the user to request a new one.
 * - 422: the password failed the server policy, the phone failed E.164
 *   normalization, or a required name or phone is missing; the server's
 *   `detail` is shown.
 */
export function SetPassword(): ReactNode {
  const api = usePortalApi();
  const { apiPrefix, copy } = usePortalAuthConfig();
  const { setSession } = usePortalSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // `linkInfo` is null until link-info resolves or fails. The form is gated on
  // it, so the name and phone fields never render and then vanish mid-fetch; a
  // single sentinel keeps the `=== null` gate simple.
  const [linkInfo, setLinkInfo] = useState<PartnerLinkInfoResponse | null>(null);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    setLinkInfo(null);
    api<PartnerLinkInfoResponse>(`${apiPrefix}/auth/link-info?code=${encodeURIComponent(code)}`)
      .then((info) => {
        if (cancelled) return;
        // Seed the editable fields from the invite's stored values, so the
        // form starts prefilled and the invitee can change either.
        setName(info.prefillName);
        setPhone(info.prefillPhone);
        setLinkInfo(info);
      })
      .catch(() => {
        // A bad or unreachable probe: assume neither name nor phone is needed
        // and render the form; a bad link surfaces through the 400 on submit.
        if (!cancelled) {
          setLinkInfo({
            needsName: false,
            firstTimeSetup: false,
            prefillName: "",
            prefillPhone: "",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, apiPrefix, code]);

  const needsName = linkInfo?.needsName ?? false;
  const firstTimeSetup = linkInfo?.firstTimeSetup ?? false;
  // The name shows when the account is nameless, and also on a first-time
  // setup whose invite stored one, so the invitee can correct the prefill. A
  // reset for a named account never shows it (the server ignores it there).
  const showName = needsName || (firstTimeSetup && (linkInfo?.prefillName ?? "") !== "");

  const rules = useMemo(() => evaluatePasswordRules(password), [password]);
  const policyOk = isPasswordValid(password);
  const matches = password === confirm;
  const nameOk = !showName || name.trim().length > 0;
  // Phone is required only when shown (first-time setup). The server checks the
  // format (a 422 "invalid phone"); this only blocks an empty value.
  const phoneOk = !firstTimeSetup || phone.trim().length > 0;
  const canSubmit =
    !busy && !!code && linkInfo !== null && policyOk && matches && nameOk && phoneOk;

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!canSubmit || !code) return;
    setBusy(true);
    setError(null);
    const body: PartnerSetPasswordRequest = {
      code,
      password,
      ...(showName ? { name: name.trim() } : {}),
      ...(firstTimeSetup ? { phone: phone.trim() } : {}),
    };
    api<PartnerSetPasswordResponse>(`${apiPrefix}/auth/set-password`, {
      method: "POST",
      body: body as unknown as Record<string, unknown>,
    })
      .then((res) => {
        // First-time setup: the account isn't created until the verify step
        // accepts an OTP. Carry only the challenge and code in router state,
        // since both are short-lived or already in the URL, and stash the
        // password in the in-memory hand-off. Router state persists into
        // history.state, so keeping the plaintext password out of it means a
        // hard refresh drops it and it is never written to history. No session
        // is stored here.
        if (res.challenge) {
          setSignupPassword(password);
          // React Router v7's `navigate` returns a promise that isn't awaited.
          // `bypassSmsOtp`, not a secret either, rides along to drive the
          // verify step's bypass banner.
          void navigate("/verify-phone", {
            state: { challenge: res.challenge, code, bypassSmsOtp: res.bypassSmsOtp },
          });
          return;
        }
        // Session path (a reset, or a first-time link for an account whose
        // phone is already verified): the server mints the session directly.
        if (res.session) {
          setSession(res.session);
          void navigate("/", { replace: true });
          return;
        }
        // Neither field present, which shouldn't happen: show a generic error.
        setBusy(false);
        setError("Something went wrong. Please try again.");
      })
      .catch((err: unknown) => {
        setBusy(false);
        if (err instanceof ApiError) {
          if (err.status === 400) {
            setError(
              "This link is invalid or has expired. Please request a new password link and try again.",
            );
            return;
          }
          if (err.status === 422) {
            // Server policy rejection: show its specific human message.
            setError(err.detail);
            return;
          }
        }
        setError("Something went wrong. Please try again.");
      });
  };

  if (!code) {
    return (
      <AuthCard
        title="Set your password"
        footer={
          <Link to="/login" className="text-primary-text hover:text-ember-dark font-medium">
            Back to sign in
          </Link>
        }
      >
        <Alert
          tone="error"
          title="This password link is missing its code. Please open the link from your email again, or request a new one."
        />
      </AuthCard>
    );
  }

  // Gate the form on the link-info probe so the name and phone fields never
  // flash in and out, and the user can't submit before the link flags are known.
  if (linkInfo === null) {
    return (
      <AuthCard title="Set your password">
        <div className="text-slate-teal flex items-center gap-3">
          <Spinner /> <span>Loading…</span>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Set your password"
      subtitle={copy.setPasswordSubtitle}
      footer={
        <Link to="/login" className="text-primary-text hover:text-ember-dark font-medium">
          Back to sign in
        </Link>
      }
    >
      <form className="space-y-5" onSubmit={onSubmit} noValidate>
        {showName && (
          <Input
            label="Full name"
            required
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        )}
        {firstTimeSetup && (
          <Input
            label="Phone"
            type="tel"
            required
            autoComplete="tel"
            placeholder="+15551234567 or 5551234567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        )}
        <div>
          <Input
            label="New password"
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <PasswordChecklist rules={rules} />
        </div>
        <Input
          label="Confirm password"
          type="password"
          required
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={confirm && !matches ? "Passwords don't match." : undefined}
        />

        {error && <Alert tone="error" title={error} />}

        <Button type="submit" size="lg" className="w-full" disabled={!canSubmit}>
          {busy ? "Setting password…" : "Set password"}
        </Button>
      </form>
    </AuthCard>
  );
}
